import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import TicketComments from './TicketComments';
import TicketCommentsForm, { SubmitTicketCommentRequstAndFiles } from './TicketCommentsForm';
import { TicketCommentCreationDialogueHandlers, DialogueHandlerProps } from '../types';
import usePostSubmitTicketComment from '../../../hooks/useApi/usePostSubmitTicketComment';
import usePostSubmitTicketAttachment from '../../../hooks/useApi/usePostSubmitTicketAttachment';
import TicketCreationContext, { TicketCommentCreationCtx } from './TicketCommentCreationContext';
import TicketCommentCreationDialogue from './TicketCommentCreationDialogue';
import { useClientLogin } from '../../../providers/ClientLogin.provider';
import useDataFetchIndicator from '../../../hooks/useDataFetchIndicator';
import { useTranslation } from 'react-i18next';

type TicketCreationProps = {
  ticketDetail: vp.TicketDetailsResponse;
  refreshTicket: () => void;
};

type FC = FunctionComponent<TicketCreationProps>;

const TicketCommentsContainer: FC = (props: TicketCreationProps) => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { ticketDetail, refreshTicket } = props;
  const { Toast } = vp;
  const [showTicketCommentCreateSuccessAlert, setShowTicketCommentCreateSuccessAlert] = useState(false);
  const [showTicketCommentCreateErrorAlert, setShowTicketCommentCreateErrorAlert] = useState(false);
  const [ticketCommentCreateError, setTicketCommentCreateError] = useState('');
  const [shouldCreateTicket, setShouldCreateTicket] = useState(false);
  const [, , createTicketCommentAsync] = usePostSubmitTicketComment({});
  const [, , submitTicketAttachment] = usePostSubmitTicketAttachment({});
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [showCommentsSection, shouldShowCommentsSection] = useState(false);
  const [showTicketTips, setShowTicketTips] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'TicketsList.Details' });
  const {
    response: { LoginUserName },
  } = useClientLogin();

  const commentCreation: TicketCommentCreationCtx = {
    showTicketCommentCreateErrorAlert,
    showTicketCommentCreateSuccessAlert: false,
    ticketCommentCreateError,
    showTicketTips,
  };

  const DefaultTicketValues: SubmitTicketCommentRequstAndFiles = {
    TicketAuthor: '',
    TicketComment: '',
    TicketID: '',
    files: [],
  };
  useEffect(() => {
    if (showTicketCommentCreateSuccessAlert) {
      setShowTicketCommentCreateSuccessAlert(false);
    }
  }, [showTicketCommentCreateSuccessAlert]);
  useEffect(() => {
    if (ticketDetail.Success) {
      shouldShowCommentsSection(
        ticketDetail.Ticket.status === undefined ||
          (ticketDetail.Ticket.status.toLowerCase() !== 'solved' &&
            ticketDetail.Ticket.status.toLowerCase() !== 'closed'),
      );
      if (shouldCreateTicket) {
        setShouldCreateTicket(false);
        setShowTicketCommentCreateSuccessAlert(true);
      }
    }
  }, [ticketDetail]);

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<TicketCommentCreationDialogueHandlers>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: TicketCommentCreationDialogueHandlers = {
        create: {
          success: {
            default: () => {
              setShowTicketCommentCreateSuccessAlert(false);
              history.push('/tickets');
            },
          },
          fail: {
            default: () => {
              setShowTicketCommentCreateErrorAlert(false);
              setTicketCommentCreateError('');
            },
          },
        },
        tips: {
          success: {
            default: () => {
              setShowTicketTips(false);
            },
          },
          fail: {
            default: () => {},
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
    onCreateComment: (values: SubmitTicketCommentRequstAndFiles) => {
      const ticketComment = { ...values };
      ticketComment.TicketAuthor = LoginUserName;
      ticketComment.TicketID = id;
      ticketComment.TicketComment = values.TicketComment;
      const processFiles = async (files: File[], ticketRequest: vp.SubmitTicketCommentRequst) => {
        setDataFetchingInfo(false, 'Loading...', 'fullScreen');
        setDataFetchingInfo(true, 'Loading...', 'fullScreen');
        const createTicketCommentResponse = await createTicketCommentAsync(ticketRequest);
        const { Success, ErrorMessage } = createTicketCommentResponse;

        if (Success === true) {
          if (files) {
            for (const file of files) {
              const base64 = await vp.convertFileToBase64(file);
              if (base64) {
                await submitTicketAttachment({
                  TicketID: id,
                  TicketAttachment: base64 as string,
                  AttachmentFileName: file.name.split('.').slice(0, -1).join('.'),
                });
              }
            }
          }
          setShouldCreateTicket(true);
          refreshTicket();
        } else {
          setTicketCommentCreateError(ErrorMessage);
          setShowTicketCommentCreateErrorAlert(true);
        }
        setDataFetchingInfo(false);
      };
      void processFiles(values.files, ticketComment);
    },
    onShowTicketTips: () => {
      setShowTicketTips(true);
    },
  };

  return (
    <TicketCreationContext.Provider value={commentCreation}>
      <TicketComments ticketDetail={ticketDetail} refreshHighlight={showTicketCommentCreateSuccessAlert} />
      {showCommentsSection && (
        <>
          <TicketCommentsForm defaultValues={DefaultTicketValues} handlers={handlers} />
          <TicketCommentCreationDialogue handleDialogue={handlers.handleDialogue} />
        </>
      )}
      <Toast isVisible={showTicketCommentCreateSuccessAlert} label={t('replyToast')} />
    </TicketCreationContext.Provider>
  );
};

export default TicketCommentsContainer;
